import React, { useMemo, useState } from 'react';
import { Divider, Space, Typography } from 'antd';
import { Moment } from 'moment';
import {
	AccountingItemApprovalStates,
	Currencies,
	DATE_AND_TIME,
	ExpenseTypes,
	HireInvoiceItemStates,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { standardSort } from '@shared/utils/standardSort';
import { Values } from '@shared/utils/objectEnums';
import type { VoyageExpenseProps } from '@api/models/voyage-expense';
import Card from '@client/components/Card/Card';
import EditableTable from '@client/components/EditableTable';
import { formatDate } from '@client/utils/formatDate';
import EmptyText from '@client/components/EmptyText';
import { getFilterProps } from '@client/utils/table';
import StateTag from '@client/components/StateTag';
import { useVoyage } from './VoyageProvider/VoyageProvider';
import EuEtsExpenseDrawer from './EuExpense/EuEtsExpenseDrawer';
import FuelEuExpenseDrawer from './EuExpense/FuelEuExpenseDrawer';
import UkEtsExpenseDrawer from './UkExpense/UkEtsExpenseDrawer';

export type EuExpenseVisible = EuExpenseSubTypes | boolean;
export type EuExpenseSubTypes = 'fuelEuPenalty' | 'euEts' | 'ukEts';
type EuExpense = NonNullable<
	NonNullable<ReturnType<typeof useVoyage>['vendorExpenses']>['rows']
>[number];

const EuExpenseCard = () => {
	const {
		fixtureCurrency,
		vessel,
		vendorExpenses,
		refreshDetails,
		refreshVendorExpenses: refreshEuExpenses,
	} = useVoyage();

	const euExpenses = useMemo(() => {
		if (vendorExpenses == null) {
			return [];
		}

		return vendorExpenses.rows;
	}, [vendorExpenses]);

	const [euExpenseDrawerVisible, setEuExpenseDrawerVisible] =
		useState<EuExpenseVisible>(false);

	const [editingEuExpenseId, setEditingEuExpenseId] = useState<number | null>(
		null,
	);

	const getEuExpenseSortOrder = (expense: EuExpense): number => {
		if (expense.isEuEts) {
			return 0;
		}

		if (expense.isUkEts) {
			return 1;
		}

		if (expense.isFuelEuPenalty) {
			return 2;
		}

		return 3;
	};

	const filteredEuExpenses = useMemo(() => (
		[...euExpenses]
			.filter((expense) =>
				expense.isEuEts ||
				expense.isUkEts ||
				expense.isFuelEuPenalty
			)
			.sort((a, b) => {
				const sortOrderDifference =
					getEuExpenseSortOrder(a) - getEuExpenseSortOrder(b);

				if (sortOrderDifference !== 0) {
					return sortOrderDifference;
				}

				return a.id - b.id;
			})
	), [euExpenses]);

	const editingEuExpenses = useMemo(() => {
		const euExpense = euExpenses?.find((e) => e.id === editingEuExpenseId);

		if (euExpense?.receivable != null) {
			return {
				...euExpense,
				charterer: euExpense.receivable.chartererId,
				linerCost: {
					value: euExpense.linerCostPricePerUnit,
					currency: euExpense.currency,
					exchangeRate: euExpense.exchangeRate,
				},
			};
		}

		// Fallback
		if (euExpense == null) {
			return {
				id: null,
				charterer: null,
				linerCost: {
					value: 0,
					currency: Currencies.USD,
					exchangeRate: 1,
				},
				amount: 0,
				attachments: [],
				actions: [],
				cargoPortId: null,
				itemType: ExpenseTypes.PAYABLE,
				linerCostPricePerUnit: 0,
				value: 0,
				currency: Currencies.USD,
				exchangeRate: 1,
			};
		}

		return {
			...euExpense,
			linerCost:
        euExpense?.linerCostPricePerUnit != null ?
        	{
        		value: euExpense?.linerCostPricePerUnit,
        		currency: euExpense?.currency,
        		exchangeRate: euExpense?.exchangeRate,
        	} :
        	{
        		value: 0,
        		currency: Currencies.USD,
        		exchangeRate: 1,
        	},
		};
	}, [euExpenses, editingEuExpenseId]);

	return (
		<>
			<Card title="Emissions Cost" slim>
				{filteredEuExpenses != null && (
					<EditableTable<any, 'id'>
						keyDataIndex="id"
						editingRow={null}
						onSave={() => null}
						onEditingRowChange={(id: number | null) => {
							setEditingEuExpenseId(id);

							let subType = 'fuelEuPenalty';

							const expense = euExpenses?.find((v) => v.id === id);

							if (expense != null) {
								if (expense.isEuEts) {
									subType = 'euEts';
								} else if (expense.isUkEts) {
									subType = 'ukEts';
								}
							}

							setEuExpenseDrawerVisible(subType as EuExpenseVisible);
						}}
						size="small"
						pagination={false}
						enableEdit={(item) =>
							item.state === AccountingItemApprovalStates.PENDING
						}
						iconButtons
						actionsTitle=""
						dataSource={filteredEuExpenses}
						columns={[
							{
								title: 'Creation date',
								dataIndex: 'createdAt',
								render: (date: Moment) => formatDate(date, DATE_AND_TIME),
							},
							{
								title: 'Type',
								render: (record: VoyageExpenseProps) => {
									if (record.isUkEts) {
										return 'UK ETS';
									}

									if (record.isFuelEuPenalty) {
										return 'Fuel EU Penalty';
									}

									return 'EU ETS';
								},
							},
							{
								title: 'Amount',
								dataIndex: 'amount',
								align: 'right',
								render: (amount: number, record: VoyageExpenseProps) =>
									record.overrideAmount != null ? 
										formatCurrency(record.overrideAmount, record.currency) : 
										amount != null ? formatCurrency(amount, record.currency) : null,
							},
							{
								dataIndex: 'state',
								title: 'Status',
								render: (
									state: Values<typeof AccountingItemApprovalStates>,
									record: VoyageExpenseProps,
								) => (
									<StateTag state={record.estimated ? 'estimated' : state} />
								),
								width: 100,
								sorter: standardSort('state'),
								...getFilterProps(
									[
										HireInvoiceItemStates.PENDING,
										HireInvoiceItemStates.INVOICED,
										HireInvoiceItemStates.UNRESOLVED,
										HireInvoiceItemStates.PAID,
									],
									'select',
									'state',
									undefined,
									true,
								),
							},
						]}
						expandable={{
							expandedRowRender: (record) => (
								<Space direction="vertical">
									<Typography.Title level={5}>Internal note</Typography.Title>
									{record.note != null && record.note !== '' ? (
										record.note
									) : (
										<EmptyText />
									)}
									{record.customInvoiceId != null && (
										<>
											<b>Invoice ID:</b>
											{record.customInvoiceId}
										</>
									)}
									{record.invoiceDate != null && (
										<>
											<b>Invoice Date:</b>
											{formatDate(record.invoiceDate)}
										</>
									)}
									{record.dueDate != null && (
										<>
											<b>Due Date:</b>
											{formatDate(record.dueDate)}
										</>
									)}
									{record.currency !== fixtureCurrency && (
										<>
											<Divider />
											<b>Original Amount:</b>
											{` ${formatCurrency(record.amount, record.currency)}`}
											<br />
											<b>Exchange Rate:</b>
											{` ${record.exchangeRate} ${record.currency} = 1 ${fixtureCurrency} `}
										</>
									)}
								</Space>
							),
						}}
					/>
				)}
			</Card>
			{euExpenseDrawerVisible === 'euEts' && (
				<EuEtsExpenseDrawer
					open
					expenseData={editingEuExpenses as any}
					isIceClass={vessel?.isIceClass ?? false}
					onClose={() => {
						setEuExpenseDrawerVisible(false);
						setEditingEuExpenseId(null);
						refreshEuExpenses();
						refreshDetails();
					}}
				/>
			)}
			{euExpenseDrawerVisible === 'ukEts' && (
				<UkEtsExpenseDrawer
					open
					expenseData={editingEuExpenses as any}
					onClose={() => {
						setEuExpenseDrawerVisible(false);
						setEditingEuExpenseId(null);
						refreshEuExpenses();
						refreshDetails();
					}}
				/>
			)}
			{euExpenseDrawerVisible === 'fuelEuPenalty' && (
				<FuelEuExpenseDrawer
					open
					expenseData={editingEuExpenses as any}
					onClose={() => {
						setEuExpenseDrawerVisible(false);
						setEditingEuExpenseId(null);
						refreshDetails();
					}}
				/>
			)}
		</>
	);
};

export default EuExpenseCard;
